import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  clientListExists,
  getSortedClientOptions,
  getSortedProjectOptions,
} from '../utils/clientProjectSelectOptions';

export function useClientProjectScope() {
  const { user } = useAuth();
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');

  const clientOptions = useMemo(() => getSortedClientOptions(user), [user]);

  const projectOptions = useMemo(
    () => getSortedProjectOptions(user, selectedClientId),
    [user, selectedClientId],
  );

  const projectNeedsClientFirst = clientListExists(user) && !selectedClientId;
  const noProjectsForClient =
    Boolean(selectedClientId) && clientListExists(user) && projectOptions.length === 0;

  const clientDropdownOptions = useMemo(
    () => clientOptions.map((c) => ({ value: c.id, label: c.label })),
    [clientOptions],
  );

  const projectDropdownOptions = useMemo(() => {
    if (projectNeedsClientFirst || noProjectsForClient) return [];
    return projectOptions.map((p) => ({ value: p.id, label: p.label }));
  }, [projectNeedsClientFirst, noProjectsForClient, projectOptions]);

  const projectPlaceholder = projectNeedsClientFirst
    ? 'Select client first'
    : noProjectsForClient
      ? 'No projects for this client'
      : 'Select project';

  useEffect(() => {
    if (!selectedProjectId) return;
    if (!projectOptions.some((p) => String(p.id) === String(selectedProjectId))) {
      setSelectedProjectId('');
    }
  }, [projectOptions, selectedProjectId]);

  const onClientChange = (next) => {
    setSelectedClientId(next == null || next === '' ? '' : String(next));
    setSelectedProjectId('');
  };

  const onProjectChange = (next) => {
    setSelectedProjectId(next == null || next === '' ? '' : String(next));
  };

  const resetScope = () => {
    setSelectedClientId('');
    setSelectedProjectId('');
  };

  return {
    selectedClientId,
    selectedProjectId,
    clientOptions,
    projectOptions,
    clientDropdownOptions,
    projectDropdownOptions,
    projectNeedsClientFirst,
    noProjectsForClient,
    projectPlaceholder,
    onClientChange,
    onProjectChange,
    resetScope,
  };
}
